/**
 * Service Type Selector Component
 * Step 1 of the booking widget - Select service type
 * Uses compact bubble/chip layout for space efficiency
 */

import { useState } from 'react';
import { Info } from 'lucide-react';

interface ServiceType {
  id: string;
  name: string;
  nameEn: string | null;
  description: string | null;
  estimatedDurationMins: number;
}

interface ServiceTypeSelectorProps {
  serviceTypes: ServiceType[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  language?: 'es' | 'en';
  primaryColor?: string;
}

export function ServiceTypeSelector({
  serviceTypes,
  selectedId,
  onSelect,
  language = 'es',
  primaryColor = '#2563eb',
}: ServiceTypeSelectorProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div className="space-y-2">
      <h3 className="text-base font-semibold text-gray-900">
        {language === 'es' ? 'Selecciona el tipo de servicio' : 'Select service type'}
      </h3>

      {/* Compact chip/bubble layout */}
      <div className="flex flex-wrap gap-1.5">
        {serviceTypes.map((type) => {
          const isSelected = selectedId === type.id;
          const isHovered = hoveredId === type.id;
          const displayName = language === 'en' && type.nameEn ? type.nameEn : type.name;

          return (
            <div key={type.id} className="relative">
              <button
                onClick={() => onSelect(type.id)}
                onMouseEnter={() => setHoveredId(type.id)}
                onMouseLeave={() => setHoveredId(null)}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  isSelected
                    ? 'text-white shadow-sm'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                style={
                  isSelected
                    ? { backgroundColor: primaryColor, borderColor: primaryColor }
                    : undefined
                }
              >
                <span>{displayName}</span>
                <span className={`text-[10px] ${isSelected ? 'opacity-70' : 'text-gray-400'}`}>
                  {type.estimatedDurationMins}min
                </span>
                {type.description && (
                  <Info className={`h-3 w-3 ${isSelected ? 'opacity-60' : 'text-gray-400'}`} />
                )}
              </button>

              {/* Tooltip for description */}
              {type.description && isHovered && (
                <div className="absolute z-10 left-0 top-full mt-1 p-1.5 bg-gray-900 text-white text-[10px] rounded-md shadow-lg max-w-xs whitespace-normal">
                  {type.description}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected service summary */}
      {selectedId && (
        <div
          className="mt-2 p-1.5 rounded-md text-xs"
          style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
        >
          {language === 'es' ? 'Servicio seleccionado: ' : 'Selected service: '}
          <span className="font-medium">
            {language === 'en' && serviceTypes.find(t => t.id === selectedId)?.nameEn
              ? serviceTypes.find(t => t.id === selectedId)?.nameEn
              : serviceTypes.find(t => t.id === selectedId)?.name}
          </span>
        </div>
      )}
    </div>
  );
}
